/**
 * Time Control Manager
 *
 * Core singleton service that manages time tracking for both players
 * across all game modes (local PvP, PvAI, multiplayer).
 *
 * Design Pattern: Singleton with event emitter for state changes
 * Single Responsibility: Manage time tracking and expiration
 */

import {
  TimeControlConfig,
  TimeControlState,
  PlayerTimeState,
  TimeControlMode,
  TimeUpdateEvent,
  TimeControlSyncData,
  TimeControlPreset
} from './types';
import { isTimedPreset } from './presets';

type TimeUpdateListener = (event: TimeUpdateEvent) => void;

class TimeControlManager {
  private static instance: TimeControlManager;

  private state: TimeControlState;
  private mode: TimeControlMode;
  private intervalId: NodeJS.Timeout | null = null;
  private listeners: Set<TimeUpdateListener> = new Set();

  // Update interval (100ms for smooth countdown)
  private readonly UPDATE_INTERVAL_MS = 100;

  // Countdown sound tracking (to avoid playing same sound multiple times)
  private playedCountdownSounds: Set<number> = new Set();
  private readonly COUNTDOWN_THRESHOLDS = [10000, 5000, 4000, 3000, 2000, 1000]; // ms

  private constructor() {
    // Initialize with default OFF state
    this.state = {
      config: {
        preset: TimeControlPreset.OFF,
        initialTime: 0,
        increment: 0
      },
      playerTime: {
        lightTimeRemaining: 0,
        darkTimeRemaining: 0,
        activePlayer: null,
        isPaused: true
      },
      timeExpired: false
    };
    this.mode = 'local';
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): TimeControlManager {
    if (!TimeControlManager.instance) {
      TimeControlManager.instance = new TimeControlManager();
    }
    return TimeControlManager.instance;
  }

  /**
   * Initialize time control for a new game
   * CRITICAL: Always resets timers to full initial time, preventing 0-second starts
   */
  public initialize(config: TimeControlConfig, mode: TimeControlMode = 'local'): void {

    // CRITICAL: Clear any existing timer to prevent leaks and stale timers
    this.cleanup();

    // CRITICAL: Always create fresh state with full initial time
    // This ensures new games NEVER start with 0 seconds from previous game
    this.state = {
      config,
      playerTime: {
        lightTimeRemaining: config.initialTime,
        darkTimeRemaining: config.initialTime,
        activePlayer: null,
        isPaused: true
      },
      timeExpired: false
    };
    this.mode = mode;


    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Start timer for the active player
   */
  public startTimer(player: 'light' | 'dark'): void {
    // Don't start timer if time control is OFF
    if (!isTimedPreset(this.state.config.preset)) {
      return;
    }


    // Stop any existing timer
    this.stopInterval();

    // Reset countdown sounds for this player's turn
    this.playedCountdownSounds.clear();

    // Set active player and unpause
    this.state.playerTime.activePlayer = player;
    this.state.playerTime.isPaused = false;

    // Start countdown interval
    this.intervalId = setInterval(() => {
      this.tick();
    }, this.UPDATE_INTERVAL_MS);

    this.notifyListeners();
  }

  /**
   * Pause the timer
   */
  public pauseTimer(): void {

    this.stopInterval();
    this.state.playerTime.isPaused = true;

    this.notifyListeners();
  }

  /**
   * Resume the timer
   */
  public resumeTimer(): void {
    if (!this.state.playerTime.activePlayer) {
      return;
    }


    this.state.playerTime.isPaused = false;

    // Restart interval
    this.intervalId = setInterval(() => {
      this.tick();
    }, this.UPDATE_INTERVAL_MS);

    this.notifyListeners();
  }

  /**
   * Handle turn change (stop current player, add increment, start next player)
   */
  public handleTurnChange(fromPlayer: 'light' | 'dark', toPlayer: 'light' | 'dark'): void {
    // Don't process turn changes if time control is OFF
    if (!isTimedPreset(this.state.config.preset)) {
      return;
    }


    // Stop current timer
    this.stopInterval();

    // Add increment to the player who just moved
    const incrementMs = this.state.config.increment;
    if (fromPlayer === 'light') {
      this.state.playerTime.lightTimeRemaining += incrementMs;
      this.state.playerTime.lightLastMoveTime = Date.now();
    } else {
      this.state.playerTime.darkTimeRemaining += incrementMs;
      this.state.playerTime.darkLastMoveTime = Date.now();
    }


    // Start timer for next player
    this.startTimer(toPlayer);
  }

  /**
   * Manually deduct time for AI thinking (since timer can't tick while AI computes)
   * This is needed because JavaScript is single-threaded and minimax blocks the event loop
   */
  public deductTime(player: 'light' | 'dark', milliseconds: number): void {

    if (player === 'light') {
      this.state.playerTime.lightTimeRemaining -= milliseconds;
      if (this.state.playerTime.lightTimeRemaining < 0) {
        this.state.playerTime.lightTimeRemaining = 0;
      }
    } else {
      this.state.playerTime.darkTimeRemaining -= milliseconds;
      if (this.state.playerTime.darkTimeRemaining < 0) {
        this.state.playerTime.darkTimeRemaining = 0;
      }
    }


    // Notify UI of the change
    this.notifyListeners();

    // Check if time expired after deduction
    this.checkTimeExpired();
  }

  /**
   * Check if time has expired for active player
   */
  private checkTimeExpired(): void {
    // Untimed presets (OFF/ANY) start at 0ms — never treat that as a timeout.
    if (!isTimedPreset(this.state.config.preset)) return;

    const { activePlayer, lightTimeRemaining, darkTimeRemaining } = this.state.playerTime;

    if (!activePlayer) return;

    const timeRemaining = activePlayer === 'light' ? lightTimeRemaining : darkTimeRemaining;

    if (timeRemaining <= 0) {

      // Stop the timer
      this.stopInterval();

      // Set time expired state
      this.state.timeExpired = true;
      this.state.winner = activePlayer === 'light' ? 'dark' : 'light';
      this.state.playerTime.isPaused = true;

      // Set time to exactly 0 (not negative)
      if (activePlayer === 'light') {
        this.state.playerTime.lightTimeRemaining = 0;
      } else {
        this.state.playerTime.darkTimeRemaining = 0;
      }

      this.notifyListeners();
    }
  }

  /**
   * Timer tick (called every UPDATE_INTERVAL_MS)
   */
  private tick(): void {
    const { activePlayer, isPaused } = this.state.playerTime;

    if (isPaused || !activePlayer) return;

    // Get time before decrement
    const timeBefore = activePlayer === 'light'
      ? this.state.playerTime.lightTimeRemaining
      : this.state.playerTime.darkTimeRemaining;

    // Decrement time for active player
    if (activePlayer === 'light') {
      this.state.playerTime.lightTimeRemaining -= this.UPDATE_INTERVAL_MS;
    } else {
      this.state.playerTime.darkTimeRemaining -= this.UPDATE_INTERVAL_MS;
    }

    // Get time after decrement
    const timeAfter = activePlayer === 'light'
      ? this.state.playerTime.lightTimeRemaining
      : this.state.playerTime.darkTimeRemaining;

    // Check for countdown sound thresholds
    this.checkCountdownSounds(timeBefore, timeAfter);

    // Check for time expiration
    this.checkTimeExpired();

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Check if we crossed a countdown threshold and play sound
   */
  private checkCountdownSounds(timeBefore: number, timeAfter: number): void {
    for (const threshold of this.COUNTDOWN_THRESHOLDS) {
      // Check if we just crossed this threshold (time went from above to at/below)
      if (timeBefore > threshold && timeAfter <= threshold && !this.playedCountdownSounds.has(threshold)) {
        this.playedCountdownSounds.add(threshold);
        this.playCountdownSound(threshold);
        break; // Only play one sound per tick
      }
    }
  }

  /**
   * Play countdown sound
   */
  private playCountdownSound(threshold: number): void {
    try {
      // Use single timerblip sound for all countdown thresholds (10s, 5, 4, 3, 2, 1)
      const audio = new Audio('/timerblip.mp3');
      audio.volume = 0.2; // Subtle volume
      audio.play().catch(() => {
        // Ignore autoplay errors (user hasn't interacted with page yet)
      });
    } catch (e) {
      // Ignore errors in environments without Audio
    }
  }

  /**
   * Stop the countdown interval
   */
  private stopInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Get current time remaining for a player
   */
  public getTimeRemaining(player: 'light' | 'dark'): number {
    return player === 'light'
      ? this.state.playerTime.lightTimeRemaining
      : this.state.playerTime.darkTimeRemaining;
  }

  /**
   * Get full time control state
   */
  public getState(): TimeControlState {
    return { ...this.state };
  }

  /**
   * Subscribe to time updates
   */
  public subscribe(listener: TimeUpdateListener): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const event: TimeUpdateEvent = {
      lightTimeRemaining: this.state.playerTime.lightTimeRemaining,
      darkTimeRemaining: this.state.playerTime.darkTimeRemaining,
      activePlayer: this.state.playerTime.activePlayer,
      isPaused: this.state.playerTime.isPaused,
      timeExpired: this.state.timeExpired,
      winner: this.state.winner
    };

    this.listeners.forEach(listener => listener(event));
  }

  /**
   * Sync with server time (multiplayer mode)
   */
  public syncWithServerTime(syncData: TimeControlSyncData, serverTimestamp: number): void {
    if (this.mode !== 'multiplayer') {
      return;
    }

    const now = Date.now();
    const elapsedSinceUpdate = now - syncData.lastMoveTimestamp;

    // CRITICAL: Skip sync if timestamp is too old (> 30 seconds)
    // This prevents stale data from previous games from corrupting time
    if (elapsedSinceUpdate > 30000) {
      return;
    }


    const latency = now - serverTimestamp;

    // Update times from server
    this.state.playerTime.lightTimeRemaining = syncData.lightTimeRemaining;
    this.state.playerTime.darkTimeRemaining = syncData.darkTimeRemaining;
    this.state.playerTime.activePlayer = syncData.activePlayer;

    // Adjust for latency (subtract elapsed time since server update)
    if (syncData.activePlayer === 'light') {
      this.state.playerTime.lightTimeRemaining -= elapsedSinceUpdate;
    } else {
      this.state.playerTime.darkTimeRemaining -= elapsedSinceUpdate;
    }

    // CRITICAL: Clamp times to prevent negative values
    this.state.playerTime.lightTimeRemaining = Math.max(0, this.state.playerTime.lightTimeRemaining);
    this.state.playerTime.darkTimeRemaining = Math.max(0, this.state.playerTime.darkTimeRemaining);

    // Safety: if latency adjustment drove a time to 0 but the server value was positive,
    // give a tiny buffer so tick() can confirm the timeout with fully consistent state.
    // This prevents false timeouts caused by stale sync data or network latency.
    if (this.state.playerTime.lightTimeRemaining === 0 && syncData.lightTimeRemaining > 0) {
      this.state.playerTime.lightTimeRemaining = 100;
    }
    if (this.state.playerTime.darkTimeRemaining === 0 && syncData.darkTimeRemaining > 0) {
      this.state.playerTime.darkTimeRemaining = 100;
    }


    this.notifyListeners();
  }

  /**
   * Stop timer due to game end (for any win condition except timeout)
   */
  public stopOnGameEnd(): void {

    this.stopInterval();
    this.state.playerTime.isPaused = true;

    this.notifyListeners();
  }

  /**
   * Reset timer (for new game or rematch)
   */
  public reset(): void {

    this.cleanup();

    this.state.playerTime.lightTimeRemaining = this.state.config.initialTime;
    this.state.playerTime.darkTimeRemaining = this.state.config.initialTime;
    this.state.playerTime.activePlayer = null;
    this.state.playerTime.isPaused = true;
    this.state.timeExpired = false;
    this.state.winner = undefined;

    this.notifyListeners();
  }

  /**
   * Cleanup (stop timers, clear state)
   */
  public cleanup(): void {
    this.stopInterval();
    this.listeners.clear();
  }
}

// Export singleton instance
export default TimeControlManager.getInstance();
